import "./style.css";
import { chat, getStatus, deleteSession } from "./api.js";
import { getIdToken } from "../../auth/cognito.js";

const ICONS = {
  logo: '<path d="M12 3 3 8v8l9 5 9-5V8zM3 8l9 5 9-5M12 13v8M7.5 5.5l9 5"/>',
  expand: '<path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M3 3l6 6m12-6-6 6M3 21l6-6m12 6-6-6"/>',
  close: '<path d="m6 6 12 12M6 18 18 6"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  arrow: '<path d="M4 12h16m-6-6 6 6-6 6"/>',
  send: '<path d="m12 4 7 7m-7-7-7 7m7-7v16"/>',
  stop: '<rect x="6" y="6" width="12" height="12" rx="1"/>',
};
const FILTER_LABELS = {
  marina_id: "Project code", space_type: "Inventory type", region: "Region",
  out_of_service: "Out of service", covered_yn: "Covered", boatlift_yn: "Boat lift",
  fueldock_yn: "Fuel dock", multislip_yn: "Multi slip",
  measured_linear_feet: "Measured length (ft)", measured_width_feet: "Measured width (ft)",
  measured_depth_feet: "Recorded depth (ft)",
};
function node(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text != null) element.textContent = String(text);
  return element;
}
function icon(name) {
  const wrap = node("span");
  wrap.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name]}</svg>`;
  return wrap;
}
function button(label, className, action, iconName) {
  const element = node("button", className);
  element.type = "button";
  element.title = label;
  element.setAttribute("aria-label", label);
  if (iconName) element.append(icon(iconName));
  if (!className.includes("icon-button")) element.append(node("span", "", label));
  element.addEventListener("click", action);
  return element;
}
function scopeKey(scope) { return `${scope.clientId || ""}|${scope.projectCode || ""}`; }
function formatCount(value) { return Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—"; }
function readable(value) { return value == null ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value); }
function fieldLabel(field, columns = []) {
  return columns.find(column => column.key === field)?.label || FILTER_LABELS[field]
    || String(field || "Field").replaceAll("_", " ").replace(/^./, letter => letter.toUpperCase());
}
function conditionLabel(condition, columns) {
  if (!condition || typeof condition !== "object") return null;
  const field = condition.label || fieldLabel(condition.field, columns);
  const units = condition.unit && !String(field).toLowerCase().includes(`(${String(condition.unit).toLowerCase()})`) ? ` (${condition.unit})` : "";
  const label = `${field}${units}`;
  const op = String(condition.op || "eq").toLowerCase();
  if (["is_null", "is_missing"].includes(op)) return `${label}: missing`;
  if (["is_not_null", "not_null", "is_present"].includes(op)) return `${label}: has a value`;
  const value = Array.isArray(condition.value) ? condition.value.map(readable).join(", ") : readable(condition.value);
  const operators = { eq: "=", ne: "≠", neq: "≠", gt: ">", gte: "≥", lt: "<", lte: "≤", contains: "contains", starts_with: "starts with", in: "is one of", not_in: "is not one of" };
  if (op === "between" && Array.isArray(condition.value)) return `${label}: between ${condition.value.map(readable).join(" and ")}`;
  return `${label} ${operators[op] || op.replaceAll("_", " ")} ${value}`;
}
function download(contents, type, filename) {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const link = document.createElement("a");
  link.href = url; link.download = filename;
  document.body.append(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function csvCell(value) {
  let text = value == null ? "" : readable(value);
  // Spreadsheet apps interpret these prefixes as formulas even in quoted CSV.
  if (/^[\s]*[=+@-]/.test(text)) text = `'${text}`;
  return `"${text.replaceAll('"', '""')}"`;
}
async function openAsset(body, signal) {
  const token = await getIdToken();
  if (!token) throw new Error("Your session has expired. Sign in again to open project assets.");
  const response = await fetch("/api/geoai/assets/open", {
    method: "POST", signal,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) throw new Error(payload.error || payload.message || "This project asset is unavailable. Refresh the results and try again.");
  return payload;
}
function sourceUrl(value) {
  let url;
  try { url = new URL(value); } catch { throw new Error("The source link could not be verified."); }
  const awsS3Host = /^(?:[a-z0-9][a-z0-9.-]*\.)?s3(?:\.dualstack)?(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i;
  if (url.protocol !== "https:" || url.username || url.password || url.hash || url.port && url.port !== "443" || !awsS3Host.test(url.hostname)) {
    throw new Error("The source link could not be verified.");
  }
  return url.href;
}
const VIEWER_LABELS = { surfaces: "Surfaces", "point-cloud-workspace": "Point Cloud Workspace", "digital-asset-manager": "Project assets" };

function createRuntime(app) {
  let panel = null;
  let root = null;
  let refs = {};
  let sessionId = null;
  let requestController = null;
  let statusController = null;
  let requestEpoch = 0;
  let connection = null;
  let busy = false;
  let scopeMode = app.services.context?.getActiveProject?.() ? "project" : "portfolio";
  let scope = readScope();
  let dialog = null;
  let placeholder = null;
  let returnFocus = null;
  let turns = [];
  let error = "";
  let statusText = "";
  const mapLayers = new Map();
  let destroyed = false;
  let actionEpoch = 0;
  const assetActions = new Set();
  const subscriptions = [
    app.events?.on?.("context:changed", syncContext),
    app.events?.on?.("context:catalog:changed", syncContext),
  ].filter(Boolean);

  function readScope() {
    const client = app.services.context?.getActiveClient?.();
    const project = app.services.context?.getActiveProject?.();
    const projectCode = scopeMode === "project" && project ? project.code || project.marinaId || project.id : null;
    return {
      clientId: client?.id || client?.slug || null,
      projectCode,
      label: projectCode ? project.name || projectCode : `${client?.name || "Selected client"} · portfolio`,
    };
  }
  function clearMap() {
    for (const id of mapLayers.values()) {
      try { app.services.layers.removeLayer(id); } catch {}
    }
    mapLayers.clear();
  }
  function cancelAssetActions() {
    actionEpoch++;
    for (const action of assetActions) { action.controller.abort(); action.popup?.close(); }
    assetActions.clear();
  }
  function resetConversation({ retainDraft = false } = {}) {
    cancelAssetActions();
    requestEpoch++;
    requestController?.abort(); requestController = null;
    busy = false;
    const previousSession = sessionId;
    sessionId = null;
    turns = []; error = ""; statusText = "";
    clearMap();
    deleteSession(previousSession).catch(() => {});
    if (refs.input && !retainDraft) { refs.input.value = ""; resizeInput(); }
    if (refs.conversation) {
      refs.conversation.replaceChildren(welcome());
      refs.progress.hidden = true;
      updateControls();
    }
  }
  function syncContext() {
    if (destroyed) return;
    if (!app.services.context?.getActiveProject?.() && scopeMode === "project") scopeMode = "portfolio";
    const next = readScope();
    if (scopeKey(next) !== scopeKey(scope)) resetConversation();
    scope = next;
    updateScope(); updateControls();
  }
  function updateScope() {
    if (!refs.scope) return;
    const project = app.services.context?.getActiveProject?.();
    const client = app.services.context?.getActiveClient?.();
    refs.scope.replaceChildren();
    const projectOption = node("option", "", project ? `Current project · ${project.name || project.code}` : "Current project · select a project first");
    projectOption.value = "project"; projectOption.disabled = !project;
    const portfolioOption = node("option", "", `${client?.name || "Selected client"} · entire portfolio`);
    portfolioOption.value = "portfolio";
    refs.scope.append(projectOption, portfolioOption);
    refs.scope.value = scopeMode;
    refs.scope.disabled = !client;
  }
  function welcome() {
    const welcomeEl = node("div", "geoai__welcome");
    welcomeEl.append(node("div", "geoai__eyebrow", "Your spatial workspace"));
    welcomeEl.append(node("h3", "", "Start with a question."));
    const modelConnected = connection?.mode === "ollama" && connection.ready !== false;
    welcomeEl.append(node("p", "", modelConnected
      ? "Discover available project data, compare measurements, and explore camera coverage and activity. Inspect query conditions and missing values, then bring matching locations onto the map."
      : "Explore live inventory and projects. Inspect results, refine your search, and bring matching features onto the map."));
    const prompts = node("div", "geoai__prompts");
    const suggestions = modelConnected ? [
      ["Explore available data", "What datasets are available in this scope?"],
      ["Explore project files", "What files are available for the current project?"],
      ["Find point clouds and surfaces", "Which projects have LAS files or surfaces?"],
      ["Review registered deliverables", "What deliverables are registered for LLM?"],
      ["Check inventory exports", "Which projects are missing an inventory CSV?"],
      ["Find longer slips", "Show inventory records with measured length greater than 40 feet"],
      ["Check missing depths", "Which marinas have the most inventory records with missing depth measurements?"],
      ["Explore camera coverage", "Which projects have camera frames, and when were they captured?"],
      ["Review project activity", "Show recent activity in this scope"],
      ["Count travel lifts", "How many travel lifts are in this scope?"],
    ] : [
      ["Count travel lifts", "How many travel lifts are in this scope?"],
      ["Compare marinas", "Group inventory by marina"],
      ["Find on the map", "Show travel lifts on the map"],
    ];
    suggestions.forEach(([label, prompt]) => {
      const item = button(label, "geoai__prompt", () => {
        refs.input.value = prompt; resizeInput(); updateControls(); refs.input.focus();
      });
      item.append(icon("arrow")); prompts.append(item);
    });
    welcomeEl.append(prompts);
    return welcomeEl;
  }
  function build() {
    root = node("section", "geoai");
    root.setAttribute("aria-label", "GEOAI assistant");
    const header = node("header", "geoai__header");
    const logo = node("div", "geoai__logo"); logo.append(icon("logo"));
    const identity = node("div", "geoai__identity");
    identity.append(node("h2", "geoai__title", "GEOAI"), node("div", "geoai__subtitle", "HydroTools assistant · preview"));
    refs.newChat = button("New chat", "geoai__icon-button", () => { resetConversation(); refs.input.focus(); }, "plus");
    refs.expand = button("Expand chat", "geoai__icon-button", () => dialog ? closeExpanded() : expand(), "expand");
    header.append(logo, identity, refs.newChat, refs.expand);
    const connectionEl = node("div", "geoai__connection");
    refs.dot = node("span", "geoai__dot");
    refs.connection = node("span", "", "Connecting to GEOAI…");
    refs.connection.setAttribute("role", "status");
    refs.retry = button("Reconnect", "geoai__retry", loadStatus); refs.retry.hidden = true;
    connectionEl.append(refs.dot, refs.connection, refs.retry);
    refs.connectionMessage = node("p", "geoai__connection-message"); refs.connectionMessage.hidden = true;
    const scopeEl = node("div", "geoai__scope");
    const scopeLabel = node("label", "", "Search scope");
    refs.scope = node("select"); refs.scope.id = "geoai-scope"; scopeLabel.htmlFor = refs.scope.id;
    refs.scope.addEventListener("change", () => {
      scopeMode = refs.scope.value;
      resetConversation(); scope = readScope(); updateScope(); updateControls();
    });
    scopeEl.append(scopeLabel, refs.scope);
    refs.conversation = node("div", "geoai__conversation");
    refs.conversation.setAttribute("role", "log"); refs.conversation.setAttribute("aria-label", "Conversation");
    refs.conversation.setAttribute("aria-live", "polite"); refs.conversation.setAttribute("aria-relevant", "additions");
    refs.conversation.append(welcome());
    refs.progress = node("div", "geoai__progress"); refs.progress.hidden = true;
    refs.progress.append(node("span", "geoai__spinner"));
    refs.progressText = node("span"); refs.progress.append(refs.progressText);
    refs.progress.setAttribute("role", "status");
    refs.error = node("p", "geoai__error"); refs.error.hidden = true; refs.error.setAttribute("role", "alert");
    const composer = node("form", "geoai__composer");
    composer.addEventListener("submit", (e) => { e.preventDefault(); send(); });
    const inputWrap = node("div", "geoai__input-wrap");
    refs.input = node("textarea"); refs.input.placeholder = "Ask about project data…";
    refs.input.rows = 2; refs.input.maxLength = 4000;
    refs.input.setAttribute("aria-label", "Message GEOAI");
    refs.input.addEventListener("input", () => { resizeInput(); updateControls(); });
    refs.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey && !e.isComposing) { e.preventDefault(); send(); }
    });
    const composerRow = node("div", "geoai__composer-row");
    composerRow.append(node("span", "geoai__composer-hint", "Enter to send · Shift + Enter for a new line"));
    refs.send = button("Send", "geoai__button geoai__button--primary geoai__send", send, "send");
    refs.stop = button("Stop", "geoai__button geoai__send", stop, "stop"); refs.stop.hidden = true;
    composerRow.append(refs.stop, refs.send);
    inputWrap.append(refs.input, composerRow);
    composer.append(inputWrap, node("p", "geoai__disclaimer", "Results are sourced from live project data."));
    root.append(header, connectionEl, refs.connectionMessage, scopeEl, refs.conversation, refs.error, composer);
    updateScope(); updateControls();
  }
  function resizeInput() {
    refs.input.style.height = "auto";
    refs.input.style.height = `${Math.min(150, refs.input.scrollHeight)}px`;
  }
  function updateControls() {
    if (!root) return;
    refs.send.disabled = busy || !scope.clientId || !connection?.ok || connection.ready === false || !refs.input.value.trim();
    refs.send.hidden = busy; refs.stop.hidden = !busy;
    refs.newChat.disabled = !turns.length && !busy;
    refs.error.textContent = error; refs.error.hidden = !error;
    refs.input.placeholder = scope.clientId ? "Ask about project data…" : "Select a client to start…";
  }
  function updateConnection() {
    const ready = connection?.ok && connection.ready !== false;
    refs.dot.dataset.state = ready ? "ready" : "error";
    refs.connection.textContent = connection?.ok
      ? connection.mode === "ollama" ? `${connection.model || "GEOAI model"} · ${ready ? "connected" : "unavailable"}` : `Live data tools · ${ready ? "model pending" : "unavailable"}`
      : "GEOAI is unavailable";
    refs.connectionMessage.textContent = !ready && connection?.message ? String(connection.message) : "";
    refs.connectionMessage.hidden = !refs.connectionMessage.textContent;
    refs.retry.hidden = !!ready;
    if (!turns.length && !busy) refs.conversation.replaceChildren(welcome());
    updateControls();
  }
  async function loadStatus() {
    statusController?.abort();
    const controller = new AbortController(); statusController = controller;
    if (refs.connection) { refs.connection.textContent = "Connecting to GEOAI…"; refs.connectionMessage.hidden = true; }
    try {
      const data = await getStatus(controller.signal);
      if (destroyed || controller !== statusController) return;
      connection = data; error = ""; updateConnection();
    } catch (err) {
      if (controller.signal.aborted || destroyed || controller !== statusController) return;
      connection = null; error = err.message || "Unable to connect to GEOAI."; updateConnection();
    }
  }
  function scrollToLatest(force = false) {
    const el = refs.conversation;
    if (force || el.scrollHeight - el.scrollTop - el.clientHeight < 170) el.scrollTop = el.scrollHeight;
  }
  function appendTurn(role, text = "") {
    if (!turns.length) refs.conversation.replaceChildren();
    const el = node("article", "geoai__turn"); el.dataset.role = role;
    el.append(node("div", "geoai__turn-label", role === "user" ? "You" : "GEOAI"));
    const body = node("div", "geoai__text", text); el.append(body);
    const turn = { role, text, el, body }; turns.push(turn);
    refs.conversation.append(el); scrollToLatest(true);
    return turn;
  }
  function setProgress(text) {
    statusText = text;
    refs.progressText.textContent = text; refs.progress.hidden = !text;
    refs.conversation.append(refs.progress); scrollToLatest();
  }
  async function send() {
    const message = refs.input.value.trim();
    if (!message || busy || !scope.clientId || !connection?.ok || connection.ready === false) return;
    const epoch = ++requestEpoch;
    const requestScope = { clientId: scope.clientId, projectCode: scope.projectCode };
    const controller = new AbortController(); requestController = controller;
    let completed = false;
    let assistant = null;
    busy = true; error = "";
    appendTurn("user", message);
    refs.input.value = ""; resizeInput(); updateControls();
    setProgress("Checking live data…");
    try {
      await chat({ message, scope: requestScope, ...(sessionId ? { sessionId } : {}) }, {
        signal: controller.signal,
        onEvent(type, payload) {
          if (destroyed || epoch !== requestEpoch || controller.signal.aborted) return;
          if (type === "error") throw new Error(payload.message || "GEOAI could not finish this request.");
          if (type === "status") setProgress(payload.message || "Working…");
          if (type === "tool_start") setProgress(payload.label || "Searching live data…");
          if (type === "result") {
            if (payload.scope?.clientId && scopeKey(payload.scope) !== scopeKey(requestScope)) {
              throw new Error("The result scope changed. Start a new search.");
            }
            if (!assistant) assistant = appendTurn("assistant");
            assistant.el.append(resultCard(payload)); scrollToLatest();
          }
          if (type === "delta" || type === "message") {
            if (!assistant) assistant = appendTurn("assistant");
            assistant.text = type === "delta" ? assistant.text + (payload.text || "") : payload.text || "";
            assistant.body.textContent = assistant.text; scrollToLatest();
          }
          if (type === "done") {
            completed = true;
            sessionId = payload.sessionId || sessionId;
            if (payload.mode) connection = { ...connection, mode: payload.mode, model: payload.model };
            updateConnection();
          }
        },
      });
      if (!completed && epoch === requestEpoch) throw new Error("The connection ended before GEOAI finished. Try again.");
    } catch (err) {
      if (epoch !== requestEpoch || destroyed) return;
      if (!controller.signal.aborted) error = err.message || "GEOAI could not complete this request.";
    } finally {
      if (epoch === requestEpoch && !destroyed) {
        busy = false; requestController = null; setProgress(""); updateControls();
      }
    }
  }
  function stop() {
    cancelAssetActions();
    requestController?.abort(); requestEpoch++;
    requestController = null; busy = false;
    setProgress("");
    const last = turns.at(-1);
    if (last?.role === "assistant") last.el.append(node("div", "geoai__result-summary", "Response stopped."));
    else appendTurn("assistant", "Request stopped.");
    // The server may have received the request before cancellation; discard its
    // session so follow-ups cannot refer to a result the browser did not receive.
    const previousSession = sessionId; sessionId = null; deleteSession(previousSession).catch(() => {});
    updateControls(); refs.input.focus();
  }
  function openProjectWorkspace(viewer, requestScope) {
    if (!viewer || !Object.hasOwn(VIEWER_LABELS, viewer.toolId)) throw new Error("This asset has no supported project workspace.");
    const code = String(viewer.projectCode || "").trim().toUpperCase();
    const context = app.services.context;
    const projects = context?.getProjects?.(requestScope.clientId) || context?.getAllProjects?.() || [];
    const project = projects.find(item => item.clientId === requestScope.clientId && String(item.code || item.marinaId || "").trim().toUpperCase() === code);
    if (!project || requestScope.projectCode && code !== String(requestScope.projectCode).toUpperCase()) throw new Error("The asset project is outside the selected scope.");
    const tools = app.tools || (window.__hydrotools?.services?.context === context ? window.__hydrotools.tools : null);
    if (!tools?.setToolEnabled || !context?.setActiveProject) throw new Error("Open this project's assets from Dashboard Builder to view the file.");
    if (dialog) closeExpanded();
    if (!tools.setToolEnabled(viewer.toolId, true, { silent: true })) throw new Error("The project workspace is unavailable for this account.");
    context.setActiveProject(project.id);
    const details = document.querySelector(`details[data-tool-id="${viewer.toolId}"]`);
    if (!details) throw new Error("The project workspace could not be opened.");
    for (let parent = details.parentElement; parent; parent = parent.parentElement) {
      if (parent.tagName === "DETAILS") parent.open = true;
      if (parent.dataset?.dockRoot) app.ui?.setDockOpen?.(parent.dataset.dockRoot, true);
    }
    details.open = true; details.scrollIntoView({ block: "nearest" });
    details.querySelector("summary")?.focus();
    const message = `${VIEWER_LABELS[viewer.toolId]} is open for ${project.name || code}. Select ${viewer.fileName || "the asset"} in the workspace.`;
    app.ui?.toast?.(message);
    return message;
  }
  async function runAssetAction(action, control, notice) {
    if (destroyed || !control.isConnected || !["open_source", "open_viewer"].includes(action?.type) || typeof action.ref !== "string" || !action.ref || action.ref.length > 2048) return;
    const epoch = actionEpoch;
    const requestScope = { clientId: scope.clientId, projectCode: scope.projectCode };
    const pending = { controller: new AbortController(), popup: null };
    const current = () => !destroyed && epoch === actionEpoch && !pending.controller.signal.aborted && scopeKey(requestScope) === scopeKey(scope);
    control.disabled = true; notice.textContent = "Opening project asset…"; notice.dataset.state = "loading";
    try {
      if (action.type === "open_source") {
        pending.popup = window.open("about:blank", "_blank");
        if (!pending.popup) throw new Error("Allow popups for HydroTools, then open the source again.");
        pending.popup.opener = null;
      }
      assetActions.add(pending);
      const response = await openAsset({ scope: requestScope, ref: action.ref, mode: action.type === "open_source" ? "source" : "viewer" }, pending.controller.signal);
      if (!current()) { pending.popup?.close(); return; }
      if (response.expiresAt && (!Number.isFinite(Date.parse(response.expiresAt)) || Date.parse(response.expiresAt) <= Date.now())) throw new Error("The source link expired. Refresh the results and try again.");
      if (action.type === "open_source") {
        const url = sourceUrl(response.url);
        pending.popup.location.replace(url); pending.popup = null;
        notice.textContent = "Source opened in a new tab.";
      } else notice.textContent = openProjectWorkspace(response.viewer, requestScope);
      notice.dataset.state = "success";
    } catch (err) {
      pending.popup?.close();
      if (current()) { notice.textContent = err.message || "This project asset could not be opened."; notice.dataset.state = "error"; }
    } finally {
      assetActions.delete(pending);
      if (control.isConnected) control.disabled = false;
    }
  }
  function resultCard(result) {
    const card = node("section", "geoai__result");
    const head = node("div", "geoai__result-head");
    head.append(node("h3", "geoai__result-title", result.title || "Query result"));
    if (result.totalCount != null) {
      const count = node("div", "geoai__result-count", formatCount(result.totalCount));
      count.append(node("small", "", result.countLabel || "matching records")); head.append(count);
    }
    if (result.summary) head.append(node("p", "geoai__result-summary", result.summary));
    if (result.totalCount == null && result.observedCount != null) head.append(node("p", "geoai__result-note", result.kind === "asset_coverage"
      ? `${formatCount(result.observedCount)} confirmed matching projects. The complete total is unknown.`
      : `${formatCount(result.observedCount)} matching assets observed. The complete total is unknown.`));
    if (result.catalogCoverage && typeof result.catalogCoverage === "object" && result.catalogCoverage.completeProjectCount != null) {
      const coverage = result.catalogCoverage;
      const notes = node("div", "geoai__coverage"); notes.setAttribute("aria-label", "Project catalog coverage");
      notes.append(node("p", "geoai__result-note", `Catalog coverage: ${formatCount(coverage.completeProjectCount)} of ${formatCount(coverage.projectCount)} projects complete; ${formatCount(coverage.partialProjectCount)} partial; ${formatCount(coverage.unavailableProjectCount)} unavailable; ${formatCount(coverage.unknownProjectCount)} unknown.`));
      if (Number(coverage.partialProjectCount) || Number(coverage.unavailableProjectCount) || Number(coverage.unknownProjectCount)) {
        notes.append(node("p", "geoai__result-note", "A partial, unavailable, or unscanned catalog cannot establish that an asset is missing."));
        notes.dataset.state = "partial";
      }
      head.append(notes);
    }
    const rows = Array.isArray(result.rows) ? result.rows : [];
    const columns = Array.isArray(result.columns) ? result.columns : [];
    const query = result.query && typeof result.query === "object" ? result.query : {};
    const filters = result.filters && typeof result.filters === "object" && !Array.isArray(result.filters) ? result.filters : {};
    const filterList = node("div", "geoai__filters"); filterList.setAttribute("aria-label", "Applied query conditions");
    const conditions = Array.isArray(query.conditions) ? query.conditions : Array.isArray(query.filters) ? query.filters : null;
    if (conditions) conditions.forEach(condition => {
      const text = conditionLabel(condition, columns);
      if (text) filterList.append(node("span", "geoai__filter", text));
    });
    else Object.entries(filters).forEach(([field, value]) => {
      if (value == null || value === "") return;
      if (field === "group_by" && typeof value === "string" && Object.hasOwn(FILTER_LABELS, value)) {
        filterList.append(node("span", "geoai__filter", `Grouped by: ${FILTER_LABELS[value]}`));
      } else if (Object.hasOwn(FILTER_LABELS, field)) {
        filterList.append(node("span", "geoai__filter", `${FILTER_LABELS[field]}: ${readable(value)}`));
      }
    });
    const groupBy = Array.isArray(query.group_by) ? query.group_by : query.group_by ? [query.group_by] : [];
    if (groupBy.length) filterList.append(node("span", "geoai__filter", `Grouped by: ${groupBy.map(field => fieldLabel(field, columns)).join(", ")}`));
    if (filterList.childElementCount) head.append(filterList);
    if (filters.out_of_service) head.append(node("p", "geoai__result-note", "Unknown service status is excluded."));
    const coverage = Array.isArray(result.coverage) ? result.coverage : [];
    if (coverage.length) {
      const notes = node("div", "geoai__coverage"); notes.setAttribute("aria-label", "Measurement coverage");
      coverage.forEach(item => {
        if (!item || item.totalCount == null || item.presentCount == null || item.missingCount == null) return;
        const label = item.label || fieldLabel(item.field, columns);
        const units = item.unit && !String(label).toLowerCase().includes(`(${String(item.unit).toLowerCase()})`) ? ` (${item.unit})` : "";
        notes.append(node("p", "geoai__result-note", `${label}${units}: ${formatCount(item.presentCount)} of ${formatCount(item.totalCount)} matching records have values; ${formatCount(item.missingCount)} are missing.`));
      });
      if (notes.childElementCount) head.append(notes);
    }
    card.append(head);
    const previewRows = rows.slice(0, 40);
    const grouped = groupBy.length > 0 || filters.group_by || result.returnedGroupCount != null;
    const rowLabel = grouped ? "groups" : "rows";
    if (previewRows.length && columns.length) {
      const hasActions = previewRows.some(row => Array.isArray(row.actions) && row.actions.some(action => ["open_source", "open_viewer"].includes(action.type) && typeof action.ref === "string"));
      const details = node("details", "geoai__table-details"); details.open = true;
      const previewLabel = rows.length > previewRows.length ? `${formatCount(previewRows.length)} of ${formatCount(rows.length)} returned ${rowLabel}` : `${formatCount(previewRows.length)} returned ${rowLabel}`;
      details.append(node("summary", "", `View table · ${previewLabel}`));
      const wrap = node("div", "geoai__table-wrap"); const table = node("table");
      const tableHead = node("thead"); const headers = node("tr");
      columns.forEach(column => { const cell = node("th", "", column.label || column.key); cell.scope = "col"; headers.append(cell); });
      if (hasActions) { const cell = node("th", "", "Open asset"); cell.scope = "col"; headers.append(cell); }
      tableHead.append(headers); table.append(tableHead);
      const tableBody = node("tbody");
      previewRows.forEach(row => {
        const tr = node("tr");
        columns.forEach(column => { const cell = node("td", "", readable(row[column.key])); cell.title = cell.textContent; tr.append(cell); });
        if (hasActions) {
          const cell = node("td", "geoai__asset-cell");
          const actions = node("div", "geoai__asset-actions");
          const notice = node("p", "geoai__asset-notice"); notice.setAttribute("role", "status");
          (Array.isArray(row.actions) ? row.actions : []).slice(0, 2).forEach(action => {
            if (!["open_source", "open_viewer"].includes(action?.type) || typeof action.ref !== "string") return;
            let control;
            control = button(action.label || (action.type === "open_source" ? "Open source" : "Open project assets"), "geoai__button", () => runAssetAction(action, control, notice));
            actions.append(control);
          });
          cell.append(actions, notice); tr.append(cell);
        }
        tableBody.append(tr);
      });
      table.append(tableBody); wrap.append(table); details.append(wrap); card.append(details);
    }
    if (Array.isArray(result.sourceStatuses) && result.sourceStatuses.length) {
      const trace = node("details", "geoai__table-details");
      trace.append(node("summary", "", "View catalog availability"));
      const wrap = node("div", "geoai__table-wrap"); const table = node("table");
      const keys = ["project_code", "source", "status", "message"];
      const headers = node("tr"); ["Project", "Catalog", "Status", "Details"].forEach(label => { const cell = node("th", "", label); cell.scope = "col"; headers.append(cell); });
      const tableHead = node("thead"); tableHead.append(headers); table.append(tableHead);
      const body = node("tbody");
      result.sourceStatuses.slice(0, 100).forEach(item => {
        const row = node("tr"); keys.forEach(key => row.append(node("td", "", readable(key === "message" ? item.message || item.reason : item[key])))); body.append(row);
      });
      table.append(body); wrap.append(table); trace.append(wrap); card.append(trace);
      if (result.sourceStatuses.length > 100 || result.sourceStatusesTruncated) trace.append(node("p", "geoai__limit", `Showing ${formatCount(Math.min(100, result.sourceStatuses.length))} of ${formatCount(result.sourceStatuses.length)} returned catalog statuses. Additional statuses were omitted; use project coverage above to assess completeness.`));
    }
    if (result.truncated || rows.length > previewRows.length) {
      const assetResult = ["project_assets", "asset_coverage", "asset_sources"].includes(result.kind);
      const limitations = [];
      if (assetResult) {
        if (Number(result.observedCount ?? result.totalCount) > rows.length) limitations.push("Additional observed matches were omitted; narrow your search for a complete set.");
        if (result.totalCount == null) limitations.push("The catalog scan is incomplete; the complete matching total is unknown.");
        if (rows.length > previewRows.length) limitations.push("Download CSV to inspect all returned rows.");
      } else limitations.push(result.truncated ? `Additional ${rowLabel} were omitted; narrow your search for a complete set.` : `Download CSV to inspect all returned ${rowLabel}.`);
      card.append(node("p", "geoai__limit", `Showing ${formatCount(previewRows.length)} of ${formatCount(rows.length)} returned ${rowLabel}. ${limitations.join(" ")}`));
    }
    if (result.missingGeometryCount) card.append(node("p", "geoai__limit", `${formatCount(result.missingGeometryCount)} returned records have no map geometry.`));
    const footer = node("div", "geoai__result-footer");
    const geojson = result.geojson?.type === "FeatureCollection" && Array.isArray(result.geojson.features) ? result.geojson : null;
    if (geojson?.features.length) {
      const show = button(`Show on map · ${formatCount(geojson.features.length)}`, "geoai__button geoai__button--primary", () => {
        try {
          let id = mapLayers.get(result);
          if (!id) {
            id = app.services.layers.addLayer("geoai", {
              id: `geoai-${crypto.randomUUID()}`, name: `GEOAI · ${result.title || "Query result"}`,
              type: "geojson-vector", data: geojson, volatile: true,
              group: "GEOAI results", opacity: .9,
              meta: { source: "geoai", scope: result.scope || scope, queriedAt: result.source?.queriedAt },
              options: {
                pointsAsCircles: true,
                styleBuilder: () => ({ color: "#2dd4bf", fillColor: "#5eead4", fillOpacity: .35, weight: 2, radius: 6 }),
                popupBuilder: feature => {
                  const popup = node("div");
                  const properties = feature.properties || {};
                  popup.append(node("strong", "", result.title || "GEOAI result"));
                  Object.entries(properties).slice(0, 12).forEach(([key, value]) => popup.append(node("div", "", `${key}: ${readable(value)}`)));
                  return popup;
                },
              },
            });
            mapLayers.set(result, id);
          }
          app.services.layers.zoomTo(id);
          if (dialog) closeExpanded();
          show.querySelector("span").textContent = "Shown on map";
        } catch (err) { error = err.message || "Could not display this result on the map."; updateControls(); }
      });
      footer.append(show);
      footer.append(button("GeoJSON", "geoai__button", () => download(JSON.stringify(geojson, null, 2), "application/geo+json", "geoai-results.geojson")));
    }
    if (rows.length && columns.length) footer.append(button("CSV", "geoai__button", () => {
      const lines = [columns.map(c => csvCell(c.label || c.key)).join(","), ...rows.map(row => columns.map(c => csvCell(row[c.key])).join(","))];
      download(`\uFEFF${lines.join("\r\n")}`, "text/csv;charset=utf-8", "geoai-results.csv");
    }));
    const source = node("div", "geoai__source", `${result.source?.label || "Live data"}${result.scope?.label ? ` · ${result.scope.label}` : ""}`);
    const queriedAt = result.source?.queriedAt;
    if (queriedAt && Number.isFinite(Date.parse(queriedAt))) {
      const time = node("time", "", `Queried ${new Date(queriedAt).toLocaleString()}`); time.dateTime = queriedAt; source.append(time);
    }
    footer.append(source); card.append(footer);
    return card;
  }
  function expand() {
    if (dialog || !root) return;
    returnFocus = refs.expand;
    dialog = node("dialog", "geoai-dialog"); dialog.setAttribute("aria-label", "GEOAI expanded chat");
    placeholder = node("div", "geoai__expanded-placeholder");
    const message = node("div"); message.append(node("p", "", "GEOAI is open in the expanded workspace."));
    message.append(button("Return to panel", "geoai__button", closeExpanded)); placeholder.append(message);
    root.replaceWith(placeholder); dialog.append(root); document.body.append(dialog);
    dialog.addEventListener("cancel", (event) => { event.preventDefault(); closeExpanded(); });
    dialog.addEventListener("click", (event) => { if (event.target === dialog) closeExpanded(); });
    // Native dialog keeps keyboard focus inside the expanded chat and makes the
    // rest of the page inert; the map action returns to the docked panel.
    dialog.showModal();
    refs.expand.replaceChildren(icon("close")); refs.expand.title = "Return to panel"; refs.expand.setAttribute("aria-label", "Return to panel");
    refs.input.focus(); resizeInput();
  }
  function closeExpanded() {
    if (!dialog) return;
    const currentDialog = dialog; dialog = null;
    currentDialog.close();
    if (placeholder?.isConnected) placeholder.replaceWith(root);
    else panel?.append(root);
    placeholder = null; currentDialog.remove();
    refs.expand.replaceChildren(icon("expand")); refs.expand.title = "Expand chat"; refs.expand.setAttribute("aria-label", "Expand chat");
    returnFocus?.focus(); returnFocus = null; resizeInput();
  }
  return {
    mount(element) {
      panel = element;
      if (!root) build();
      if (!dialog) panel.replaceChildren(root);
      syncContext();
      if (!connection && !statusController) loadStatus();
    },
    destroy() {
      cancelAssetActions();
      destroyed = true; requestEpoch++;
      requestController?.abort(); statusController?.abort();
      deleteSession(sessionId).catch(() => {}); sessionId = null;
      closeExpanded(); clearMap();
      subscriptions.forEach(off => off());
      root?.remove(); root = null; panel = null; refs = {};
    },
  };
}

export const geoaiTool = {
  id: "geoai",
  name: "GEOAI",
  init(app) { if (!this._runtime) this._runtime = createRuntime(app); },
  destroy() { this._runtime?.destroy(); this._runtime = null; },
  renderPanel(app, panelEl) { this.init(app); this._runtime.mount(panelEl); },
};
